/**
 * <g-chat-fab> - View-level chat FAB + floating panel
 *
 * Encapsulates the Intercom-style contextual chat pattern: a circular
 * floating action button that toggles a chat panel above it. Renders
 * <wy-chat> inside the panel and <wy-notification-badge> on the FAB.
 * Vanilla toggle — no Alpine state inside.
 *
 * Per project memory: view-level chats are FAB + floating panel (NOT a
 * drawer). Main content stays interactive behind the panel. Do NOT use
 * alongside <wy-posts> in the same view — pick one collaboration surface.
 *
 * @example
 *   <g-chat-fab
 *     uid="myapp:q-a:community-room"
 *     name="Community Room"
 *     icon="comments"
 *     position="bottom-right">
 *   </g-chat-fab>
 *
 * Attributes:
 *   uid          Weavy chat UID (required) — also used by the badge
 *   name         Chat header label (default 'Chat')
 *   icon         Font Awesome name without 'fa-' (default 'comments')
 *   icon-style   'regular' | 'solid' | 'light' (default 'regular')
 *   color        DaisyUI semantic color for the FAB (default 'primary')
 *   position     'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' (default 'bottom-right')
 *   panel-width  Tailwind width class for the floating panel (default 'w-[380px]')
 *   panel-height Tailwind height class for the floating panel (default 'h-[560px]')
 *   start-open   boolean attr — open the panel on first render
 */

import { openExternalChatLink } from '../util/external-chat-links.js';

const ALLOWED_COLORS = new Set([
  'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info', 'neutral', 'ghost',
]);

const POSITIONS = {
  'bottom-right': { container: 'bottom-6 right-6', panel: 'bottom-16 right-0' },
  'bottom-left':  { container: 'bottom-6 left-6',  panel: 'bottom-16 left-0' },
  'top-right':    { container: 'top-6 right-6',    panel: 'top-16 right-0' },
  'top-left':     { container: 'top-6 left-6',     panel: 'top-16 left-0' },
};

export class GChatFab extends HTMLElement {
  constructor() {
    super();
    this._open = this.hasAttribute('start-open');
  }

  static get observedAttributes() {
    return ['uid', 'name', 'icon', 'icon-style', 'color', 'position', 'panel-width', 'panel-height'];
  }

  attributeChangedCallback() {
    if (this._root) this._render();
  }

  connectedCallback() {
    if (!this._root) {
      this._render();
    }
    // Notification deeplink: the host shell (layout.ejs) resolves a clicked
    // notification to its target page and surface, then dispatches this. The
    // panel is display:none until we open it, so nothing else can bring the
    // conversation into view. `uid` is optional — absent means "the chat on
    // this page", which is the common case since a view hosts one FAB.
    this._onOpenSurface = (e) => {
      const detail = e && e.detail;
      if (!detail || detail.surface !== 'chat') return;
      if (detail.uid && detail.uid !== this.getAttribute('uid')) return;
      this.open();
    };
    window.addEventListener('app:open-surface', this._onOpenSurface);
    this._externalLinkClick = event => openExternalChatLink(event, this);
    window.addEventListener('click', this._externalLinkClick, true);
  }

  disconnectedCallback() {
    if (this._externalLinkClick) window.removeEventListener('click', this._externalLinkClick, true);
    if (this._onOpenSurface) {
      window.removeEventListener('app:open-surface', this._onOpenSurface);
      this._onOpenSurface = null;
    }
  }

  toggle() {
    this._open = !this._open;
    this._applyOpenState();
  }

  open() { this._open = true; this._applyOpenState(); }
  close() { this._open = false; this._applyOpenState(); }

  _render() {
    const uid = this.getAttribute('uid');
    if (!uid) {
      console.warn('[g-chat-fab] missing required "uid" attribute');
      return;
    }
    const name = this.getAttribute('name') || 'Chat';
    const iconName = this.getAttribute('icon') || 'comments';
    const iconStyle = this.getAttribute('icon-style') || 'regular';
    const colorAttr = this.getAttribute('color');
    const color = ALLOWED_COLORS.has(colorAttr) ? colorAttr : 'primary';
    const positionKey = this.getAttribute('position') || 'bottom-right';
    const pos = POSITIONS[positionKey] || POSITIONS['bottom-right'];
    const panelWidth = this.getAttribute('panel-width') || 'w-[380px]';
    const panelHeight = this.getAttribute('panel-height') || 'h-[560px]';

    // Build root once
    if (!this._root) {
      this.innerHTML = '';
      this._root = document.createElement('div');
      this._root.className = `fixed ${pos.container} z-40`;
      this.appendChild(this._root);
    } else {
      this._root.className = `fixed ${pos.container} z-40`;
    }

    this._root.innerHTML = `
      <div data-panel
           class="absolute ${pos.panel} ${panelWidth} ${panelHeight} max-h-[calc(100vh-6rem)] bg-base-100 rounded-2xl shadow-2xl border border-base-300 flex flex-col overflow-hidden transition-all duration-150 origin-bottom-right"
           style="display: ${this._open ? 'flex' : 'none'}; opacity: ${this._open ? '1' : '0'};">
        <div class="flex items-center justify-between px-4 py-3 border-b border-base-200 shrink-0">
          <div class="flex items-center gap-2">
            <i class="fa-${escapeHtml(iconStyle)} fa-${escapeHtml(iconName)} text-${color}"></i>
            <p class="font-medium">${escapeHtml(name)}</p>
          </div>
          <button data-close class="btn btn-ghost btn-square btn-sm" aria-label="Close ${escapeHtml(name)}">
            <i class="fa-regular fa-xmark"></i>
          </button>
        </div>
        <div class="flex-grow overflow-hidden flex flex-col">
          <wy-chat uid="${escapeHtml(uid)}" name="${escapeHtml(name)}" class="flex h-full w-full" notifications="none" features="receipts typing"></wy-chat>
        </div>
      </div>

      <button data-toggle
              class="btn btn-${color} btn-circle shadow-lg relative"
              aria-label="${this._open ? 'Close' : 'Open'} ${escapeHtml(name)}"
              aria-expanded="${this._open}">
        <i class="fa-${escapeHtml(iconStyle)} fa-${this._open ? 'xmark' : escapeHtml(iconName)} text-lg"></i>
        <wy-notification-badge badge="dot"
          uid="${escapeHtml(uid)}"
          class="absolute top-1 right-1 pointer-events-none"></wy-notification-badge>
      </button>
    `;

    this._panel = this._root.querySelector('[data-panel]');
    this._toggleBtn = this._root.querySelector('[data-toggle]');
    this._toggleBtn.addEventListener('click', () => this.toggle());
    this._root.querySelector('[data-close]').addEventListener('click', () => this.close());
  }

  _applyOpenState() {
    if (!this._panel || !this._toggleBtn) return;
    const name = this.getAttribute('name') || 'Chat';
    const iconName = this.getAttribute('icon') || 'comments';
    const iconStyle = this.getAttribute('icon-style') || 'regular';
    if (this._open) {
      this._panel.style.display = 'flex';
      // Force reflow so the transition fires
      void this._panel.offsetHeight;
      this._panel.style.opacity = '1';
      this._panel.style.transform = 'translateY(0)';
    } else {
      this._panel.style.opacity = '0';
      this._panel.style.transform = 'translateY(8px)';
      setTimeout(() => { if (!this._open) this._panel.style.display = 'none'; }, 150);
    }
    this._toggleBtn.setAttribute('aria-expanded', String(this._open));
    this._toggleBtn.setAttribute('aria-label', `${this._open ? 'Close' : 'Open'} ${name}`);
    const icon = this._toggleBtn.querySelector('i');
    if (icon) icon.className = `fa-${iconStyle} fa-${this._open ? 'xmark' : iconName} text-lg`;
  }

  /** Dispatch on every state change for any host that wants to react */
  _emit(eventName) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, composed: true }));
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-chat-fab')) {
  customElements.define('g-chat-fab', GChatFab);
}
